import React, { useRef, useState } from "react";
import {
  InputAdornment,
  MenuItem,
  Paper,
  Popover,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { ChartBar, CalendarDots } from "@phosphor-icons/react";
import { StaticDateRangePicker } from "@mui/x-date-pickers-pro/StaticDateRangePicker";
import { format } from "date-fns";

import DateRangePickerErrorBoundary from "../../DateRangePickerErrorBoundary";
import ModuleHeader from "../ModuleHeader";
import { ModuleContainer } from "../styles";
import EmptySessionUtilization from "./EmptySessionUtilization";
import {
  PERIOD_FILTERS,
  UNIT_FILTERS,
  type TableRow as TableRowData,
  type PeriodFilter,
  type UnitFilter,
  type CustomDateRange,
} from "./types";

interface Column {
  id: keyof TableRowData | string;
  label: string;
  sx?: Record<string, unknown>;
}

const COLUMNS: Column[] = [
  { id: "client", label: "Client", sx: { width: "160px" } },
  { id: "sessionDate", label: "Session Date" },
  { id: "scheduled", label: "Scheduled" },
  { id: "delivered", label: "Delivered" },
  { id: "billable", label: "Billable" },
  { id: "billableUnitsLost", label: "Billable Units Lost" },
];

interface Props {
  rows: TableRowData[];
  period: PeriodFilter;
  onPeriodChange: (value: PeriodFilter) => void;
  unit: UnitFilter;
  onUnitChange: (value: UnitFilter) => void;
  customDateRange: CustomDateRange;
  onCustomDateChange: (range: CustomDateRange) => void;
  isSmallScreen: boolean;
}

const SessionUtilizationModule: React.FC<Props> = ({
  rows,
  period,
  onPeriodChange,
  unit,
  onUnitChange,
  customDateRange,
  onCustomDateChange,
  isSmallScreen,
}) => {
  const isEmpty = !rows || rows.length === 0;
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const inputRef = useRef<HTMLDivElement>(null);

  const handlePeriodChange = (e: SelectChangeEvent) => {
    const value = e.target.value as PeriodFilter;
    onPeriodChange(value);
    if (value === PERIOD_FILTERS.CUSTOM) {
      setDatePickerOpen(true);
    }
  };

  const formatDateRange = () => {
    const [start, end] = customDateRange;
    if (start && end) return `${format(start, "MM/dd/yyyy")} - ${format(end, "MM/dd/yyyy")}`;
    if (start) return format(start, "MM/dd/yyyy");
    return "";
  };

  const handleDateRangeChange = (newValue: CustomDateRange) => {
    onCustomDateChange(newValue);
    if (newValue[0] && newValue[1]) {
      setDatePickerOpen(false);
    }
  };

  return (
    <Paper component="section" elevation={2} sx={ModuleContainer}>
      <Stack gap={7}>
        <Stack
          direction={isSmallScreen ? "column" : "row"}
          justifyContent="space-between"
          alignItems={isSmallScreen ? "flex-start" : "center"}
          gap={3}
        >
          <ModuleHeader
            Icon={ChartBar}
            title="Session Utilization"
            subtitle={
              isEmpty ? null : (
                <>
                  Showing{" "}
                  <Typography component="span" color="primary.main" variant="subtitle2">
                    {rows.length}
                  </Typography>{" "}
                  recent sessions
                </>
              )
            }
          />

          <Stack
            direction={isSmallScreen ? "column" : "row"}
            gap={2}
            alignItems="center"
            sx={{ width: isSmallScreen ? "100%" : "auto" }}
          >
            <Select
              value={period}
              onChange={handlePeriodChange}
              size="small"
              sx={{ minWidth: 150, ...(isSmallScreen && { width: "100%" }) }}
            >
              <MenuItem value={PERIOD_FILTERS.THIS_WEEK}>This week</MenuItem>
              <MenuItem value={PERIOD_FILTERS.LAST_WEEK}>Last week</MenuItem>
              <MenuItem value={PERIOD_FILTERS.CUSTOM}>Custom range</MenuItem>
            </Select>

            <div
              ref={inputRef}
              id="session-utilization-datepicker"
              style={{
                display: period === PERIOD_FILTERS.CUSTOM ? undefined : "none",
                width: isSmallScreen ? "100%" : "auto",
              }}
            >
              <TextField
                size="small"
                value={formatDateRange()}
                placeholder="Select date range"
                onClick={(event) => {
                  // currentTarget here is TextField's root element, not the
                  // input that actually holds focus.
                  event.currentTarget.querySelector("input")?.blur();
                  setDatePickerOpen(true);
                }}
                sx={{ width: isSmallScreen ? "100%" : "auto" }}
                inputProps={{ "aria-label": "Date range", readOnly: true }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <CalendarDots size={20} />
                    </InputAdornment>
                  ),
                }}
              />
            </div>

            <Popover
              open={datePickerOpen}
              anchorEl={inputRef.current}
              onClose={() => setDatePickerOpen(false)}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              transformOrigin={{ vertical: "top", horizontal: "left" }}
            >
              <DateRangePickerErrorBoundary>
                <StaticDateRangePicker
                  value={customDateRange}
                  onChange={handleDateRangeChange as any}
                  calendars={1}
                  slotProps={{ actionBar: { actions: [] }, toolbar: { hidden: true } }}
                />
              </DateRangePickerErrorBoundary>
            </Popover>

            <Select
              value={unit}
              onChange={(e: SelectChangeEvent) => onUnitChange(e.target.value as UnitFilter)}
              size="small"
              sx={{ minWidth: 120, ...(isSmallScreen && { width: "100%" }) }}
            >
              <MenuItem value={UNIT_FILTERS.HOURS}>Hours</MenuItem>
              <MenuItem value={UNIT_FILTERS.MINUTES}>Minutes</MenuItem>
            </Select>
          </Stack>
        </Stack>

        {isEmpty ? (
          <EmptySessionUtilization />
        ) : (
          <TableContainer sx={{ overflow: "hidden", overflowX: "auto", borderRadius: "12px" }}>
            <Table aria-label="session utilization table" sx={{ minWidth: 700 }}>
              <TableHead>
                <TableRow>
                  {COLUMNS.map((col) => (
                    <TableCell key={col.id}>{col.label}</TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.videoCallId} hover>
                    <TableCell>
                      <Typography
                        component="a"
                        href={`/clients/${row.clientId}/dashboard`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          color: "#8E42CA",
                          fontSize: "14px",
                          fontWeight: 400,
                          cursor: "pointer",
                          textDecoration: "none",
                          "&:hover": { textDecoration: "underline" },
                          "&:focus-visible": {
                            textDecoration: "underline",
                            outline: "2px solid #8E42CA",
                            outlineOffset: "2px",
                          },
                        }}
                      >
                        {row.client}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {row.sessionDate}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{row.scheduled}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{row.delivered}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{row.billable}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        color={row.billableUnitsLost > 0 ? "error.main" : "success.main"}
                      >
                        {row.billableUnitsLost}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Stack>
    </Paper>
  );
};

export default SessionUtilizationModule;
